import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useAuth } from "@/hooks/useAuth";
import { useIsMobile } from "@/hooks/use-mobile";
import { supabase } from "@/integrations/supabase/client";
import {
  TrendingUp,
  TrendingDown,
  IndianRupee,
  Receipt,
  Wallet,
  Calendar,
  Sun,
  Moon,
  X,
} from "lucide-react";
import { format, startOfDay, endOfDay } from "date-fns";

interface DailySummaryPopupProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface SummaryData {
  totalSales: number;
  totalExpenses: number;
  profit: number;
  transactionCount: number;
  topService: string;
  cashSales: number;
  upiSales: number;
}

export function DailySummaryPopup({ open, onOpenChange }: DailySummaryPopupProps) {
  const isMobile = useIsMobile();
  const { tenant } = useAuth();
  const [data, setData] = useState<SummaryData | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!open || !tenant?.id) return;

    const fetchSummary = async () => {
      setIsLoading(true);
      try {
        const today = new Date();
        const startStr = startOfDay(today).toISOString();
        const endStr = endOfDay(today).toISOString();

        const [salesRes, expensesRes, saleItemsRes] = await Promise.all([
          supabase
            .from("sales")
            .select("total_amount, payment_mode")
            .eq("tenant_id", tenant.id)
            .gte("created_at", startStr)
            .lte("created_at", endStr),
          supabase
            .from("expenses")
            .select("amount")
            .eq("tenant_id", tenant.id)
            .gte("created_at", startStr)
            .lte("created_at", endStr),
          supabase
            .from("sale_items")
            .select("service_name, total_price")
            .gte("created_at", startStr)
            .lte("created_at", endStr),
        ]);

        const sales = salesRes.data || [];
        const expenses = expensesRes.data || [];
        const saleItems = saleItemsRes.data || [];

        const totalSales = sales.reduce((sum, s) => sum + (s.total_amount || 0), 0);
        const totalExpenses = expenses.reduce((sum, e) => sum + (e.amount || 0), 0);
        const cashSales = sales.filter(s => s.payment_mode === 'cash').reduce((sum, s) => sum + (s.total_amount || 0), 0);
        const upiSales = sales.filter(s => s.payment_mode === 'upi').reduce((sum, s) => sum + (s.total_amount || 0), 0);

        // Find top service
        const serviceMap = new Map<string, number>();
        saleItems.forEach(item => {
          serviceMap.set(item.service_name, (serviceMap.get(item.service_name) || 0) + item.total_price);
        });
        const topService = [...serviceMap.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] || "N/A";

        setData({
          totalSales,
          totalExpenses,
          profit: totalSales - totalExpenses,
          transactionCount: sales.length,
          topService,
          cashSales,
          upiSales,
        });
      } catch (error) {
        console.error("Error fetching summary:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSummary();
  }, [open, tenant?.id]);

  const formatCurrency = (value: number) => `₹${value.toLocaleString('en-IN')}`;
  const isEvening = new Date().getHours() >= 17;

  const content = (
    <div className="space-y-4 sm:space-y-6">
      {/* Header with greeting */}
      <div className="text-center py-3 sm:py-4 bg-gradient-to-br from-primary/10 to-primary/5 rounded-lg sm:rounded-xl">
        <div className="flex justify-center mb-1.5 sm:mb-2">
          {isEvening ? <Moon className="h-6 w-6 sm:h-8 sm:w-8 text-primary" /> : <Sun className="h-6 w-6 sm:h-8 sm:w-8 text-primary" />}
        </div>
        <h2 className="text-base sm:text-lg font-semibold text-muted-foreground">
          {isEvening ? "End of Day Summary" : "Today's Summary"}
        </h2>
        <p className="text-xs sm:text-sm text-muted-foreground flex items-center justify-center gap-1">
          <Calendar className="h-3 w-3 sm:h-4 sm:w-4" />
          {format(new Date(), "EEEE, dd MMMM yyyy")}
        </p>
      </div>

      {isLoading ? (
        <div className="space-y-3 sm:space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-16 sm:h-20 bg-muted/50 rounded-lg animate-pulse" />
          ))}
        </div>
      ) : data ? (
        <>
          {/* Main Stats */}
          <div className="grid grid-cols-1 gap-2.5 sm:gap-3">
            {/* Profit/Loss - Highlighted */}
            <Card className={`p-3 sm:p-5 border-2 ${data.profit >= 0 ? 'border-green-500/30 bg-green-500/5' : 'border-red-500/30 bg-red-500/5'}`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs sm:text-sm text-muted-foreground font-medium">
                    {data.profit >= 0 ? "Today's Profit" : "Today's Loss"}
                  </p>
                  <p className={`text-2xl sm:text-4xl font-bold ${data.profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {formatCurrency(Math.abs(data.profit))}
                  </p>
                </div>
                <div className={`h-12 w-12 sm:h-16 sm:w-16 rounded-full flex items-center justify-center ${data.profit >= 0 ? 'bg-green-500/10' : 'bg-red-500/10'}`}>
                  {data.profit >= 0 ? (
                    <TrendingUp className="h-6 w-6 sm:h-8 sm:w-8 text-green-600" />
                  ) : (
                    <TrendingDown className="h-6 w-6 sm:h-8 sm:w-8 text-red-600" />
                  )}
                </div>
              </div>
            </Card>

            {/* Sales and Expenses */}
            <div className="grid grid-cols-2 gap-2 sm:gap-3">
              <Card className="p-3 sm:p-4">
                <div className="flex items-center gap-2 sm:gap-3">
                  <div className="h-10 w-10 sm:h-12 sm:w-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Receipt className="h-5 w-5 sm:h-6 sm:w-6 text-primary" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-[10px] sm:text-xs text-muted-foreground">Total Sales</p>
                    <p className="text-base sm:text-xl font-bold text-foreground truncate">{formatCurrency(data.totalSales)}</p>
                    <p className="text-[10px] sm:text-xs text-muted-foreground">{data.transactionCount} bills</p>
                  </div>
                </div>
              </Card>

              <Card className="p-3 sm:p-4">
                <div className="flex items-center gap-2 sm:gap-3">
                  <div className="h-10 w-10 sm:h-12 sm:w-12 rounded-full bg-red-500/10 flex items-center justify-center flex-shrink-0">
                    <Wallet className="h-5 w-5 sm:h-6 sm:w-6 text-red-600" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-[10px] sm:text-xs text-muted-foreground">Expenses</p>
                    <p className="text-base sm:text-xl font-bold text-foreground truncate">{formatCurrency(data.totalExpenses)}</p>
                  </div>
                </div>
              </Card>
            </div>
          </div>

          {/* Payment Breakdown */}
          <Card className="p-3 sm:p-4">
            <p className="text-xs sm:text-sm font-semibold text-muted-foreground mb-2 sm:mb-3">Payment Breakdown</p>
            <div className="space-y-1.5 sm:space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-xs sm:text-sm">Cash</span>
                <span className="font-semibold text-sm sm:text-base">{formatCurrency(data.cashSales)}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-xs sm:text-sm">UPI</span>
                <span className="font-semibold text-sm sm:text-base">{formatCurrency(data.upiSales)}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-xs sm:text-sm">Other</span>
                <span className="font-semibold text-sm sm:text-base">{formatCurrency(data.totalSales - data.cashSales - data.upiSales)}</span>
              </div>
            </div>
          </Card>

          {/* Top Service */}
          {data.topService !== "N/A" && (
            <Card className="p-3 sm:p-4 bg-primary/5">
              <p className="text-[10px] sm:text-xs text-muted-foreground">Top Selling Service</p>
              <p className="font-semibold text-sm sm:text-lg">{data.topService}</p>
            </Card>
          )}
        </>
      ) : null}

      {/* Close Button */}
      <Button
        variant="outline"
        className="w-full h-10 sm:h-12 text-sm sm:text-base touch-manipulation"
        onClick={() => onOpenChange(false)}
      >
        Close Summary
      </Button>
    </div>
  );

  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={onOpenChange}>
        <DrawerContent className="max-h-[92vh] p-3 pb-6">
          <DrawerHeader className="p-0 pb-2">
            <DrawerTitle className="sr-only">Daily Summary</DrawerTitle>
          </DrawerHeader>
          {content}
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="fixed inset-2 sm:inset-auto sm:left-[50%] sm:top-[50%] sm:translate-x-[-50%] sm:translate-y-[-50%] w-[calc(100%-1rem)] sm:w-full sm:max-w-md h-auto max-h-[calc(100%-1rem)] sm:max-h-[90vh] flex flex-col p-4 sm:p-6 overflow-hidden rounded-lg">
        <DialogHeader className="sr-only">
          <DialogTitle>Daily Summary</DialogTitle>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto">
          {content}
        </div>
      </DialogContent>
    </Dialog>
  );
}
